import { ref } from 'vue';
import type Popover from 'primevue/popover';

/** Keeps the panel off the window edge when the trigger sits very close to it. */
const VIEWPORT_MARGIN = 8;

/**
 * Shared open/close plumbing for the top bar's dropdowns, plus the alignment PrimeVue doesn't do.
 *
 * PrimeVue left-aligns a Popover to its trigger and pins the arrow a fixed `1.25rem` from the
 * panel's left edge. Its `alignOverlay()` does work out where the arrow should sit, but writes
 * it to `--p-popover-arrow-left` while the stylesheet reads `--p-popover-arrow-offset`, so the
 * value never lands. Our triggers are against the right edge of the window, so we right-align
 * the panel to the trigger and centre the arrow under it ourselves.
 *
 * Safe as a one-shot on `show`: PrimeVue hides the panel on scroll and resize rather than
 * re-running its own alignment, so nothing overwrites this afterwards.
 */
export const useTopBarPopover = () => {
  const popover = ref<InstanceType<typeof Popover> | null>(null);
  const trigger = ref<HTMLElement | null>(null);

  const toggle = (event: MouseEvent) => {
    trigger.value = event.currentTarget as HTMLElement;
    popover.value?.toggle(event, trigger.value);
  };

  const hide = () => popover.value?.hide();

  const alignToTrigger = () => {
    // `container` is the rendered panel; it is not part of Popover's public type.
    const panel = (popover.value as unknown as { container?: HTMLElement } | null)?.container;
    const target = trigger.value;

    if (!panel || !target) {
      return;
    }

    const targetRect = target.getBoundingClientRect();
    const panelWidth = panel.offsetWidth;
    const maxLeft = document.documentElement.clientWidth - panelWidth - VIEWPORT_MARGIN;
    const left = Math.max(VIEWPORT_MARGIN, Math.min(targetRect.right - panelWidth, maxLeft));

    // The panel is absolutely positioned against the document, so it needs page coordinates.
    panel.style.left = `${left + window.scrollX}px`;
    panel.style.setProperty(
      '--p-popover-arrow-offset',
      `${targetRect.left + targetRect.width / 2 - left}px`
    );
  };

  return { popover, trigger, toggle, hide, alignToTrigger };
};
